import { useCallback } from "react";
import { CellValueChangedEvent } from "ag-grid-community";

import { showWarning } from "src/utils/toast/toastUtil";

import { TQC1050_R01Dto } from "../Dto_TQC1050_R01";

const INSPECT_VALUE_FIELDS: (keyof TQC1050_R01Dto)[] = [
	"inspectValue1_1",
	"inspectValue1_2",
	"inspectValue1_3",
	"inspectValue1_4",
];

interface UseTQC1050_R01CellChangeProps {
	updateTQC1050_R01List: (index: number, data: TQC1050_R01Dto) => void;
	originalTQC1050_R01List: TQC1050_R01Dto[];
}

/**
 * 편집 셀(검사항목 / 판정 / 조치사항)의 값 변경을 스토어에 반영하는 훅.
 *
 * <p>검사항목은 inspectValue1_1~1_4 고정 필드를 비교한다.
 * 편집 필드가 전부 원본과 같아지면 isModified 를 해제해 불필요한 저장을 막는다.
 */
export function useTQC1050_R01CellChange({
	updateTQC1050_R01List,
	originalTQC1050_R01List,
}: UseTQC1050_R01CellChangeProps) {
	const handleCellChange = useCallback(
		(event: CellValueChangedEvent<TQC1050_R01Dto>) => {
			const { data, colDef, newValue } = event;
			if (!data || !colDef.field) return;
			const field = colDef.field as keyof TQC1050_R01Dto;
			// 소문자로 입력해도 대문자로 저장한다.
			const inputValue = String(newValue ?? "").trim().toUpperCase();
			const isInvalidInspectValue =
				INSPECT_VALUE_FIELDS.includes(field) &&
				inputValue !== "" &&
				inputValue !== "O" &&
				inputValue !== "X";

			const updatedData: TQC1050_R01Dto = {
				...data,
				[field]: INSPECT_VALUE_FIELDS.includes(field)
					? (isInvalidInspectValue ? "" : inputValue)
					: newValue,
				isModified: true,
			};

			const originalRow = originalTQC1050_R01List.find(
				(item) => item.valueIndex === data.valueIndex,
			);
			if (originalRow) {
				const editableFields: (keyof TQC1050_R01Dto)[] = [
					...INSPECT_VALUE_FIELDS,
					"judgeResult",
					"remark",
				];
				const unchanged = editableFields.every(
					(field) => (originalRow[field] ?? "") === (updatedData[field] ?? ""),
				);
				if (unchanged) updatedData.isModified = false;
			}

			updateTQC1050_R01List(data.valueIndex, updatedData);
			if (isInvalidInspectValue && event.node.rowIndex !== null) {
				const rowIndex = event.node.rowIndex;
				showWarning("검사값은 O 또는 X만 입력할 수 있습니다.");
				setTimeout(() => {
					event.api.ensureIndexVisible(rowIndex);
					event.api.setFocusedCell(rowIndex, colDef.field!);
					event.api.startEditingCell({ rowIndex, colKey: colDef.field! });
				}, 0);
			}
		},
		[updateTQC1050_R01List, originalTQC1050_R01List],
	);

	return { handleCellChange };
}
